import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAnywhereTenantNotLinkedError } from './client';
import { useAnywhereScope } from './scope';

const ROOT_KEY = ['anywhere'];

export const scopedAnywhereKey = (key, scopeKey = 'client') => {
  const alreadyScoped = key[0] === ROOT_KEY[0] && (key[1] === 'client' || String(key[1]).startsWith('/anywhere'));
  return [ROOT_KEY[0], scopeKey, ...(alreadyScoped ? key.slice(2) : key.slice(1))];
};

const resourceKey = (scopeKey, ...parts) => [...ROOT_KEY, scopeKey, ...parts];

/** Stable query keys shared by all Anywhere screens. */
export const anywhereKeys = {
  root: (scopeKey = 'client') => [...ROOT_KEY, scopeKey],
  adminClientes: {
    all: () => resourceKey('admin-clientes', 'clientes'),
    list: (query = {}) => resourceKey('admin-clientes', 'clientes', 'list', query),
    detail: (idClient) => resourceKey('admin-clientes', 'clientes', 'detail', idClient),
  },
  adminMidias: {
    all: () => resourceKey('admin-midias', 'midias'),
    list: (query = {}) => resourceKey('admin-midias', 'midias', 'list', query),
    detail: (idMedia) => resourceKey('admin-midias', 'midias', 'detail', idMedia),
    clientes: (idMedia) => resourceKey('admin-midias', 'midias', 'clientes', idMedia),
  },
  /** Banco de imagens de todos os clientes: chave própria, fora de qualquer cliente selecionado. */
  adminBancoImagens: {
    list: (query = {}) => resourceKey('admin-banco-imagens', 'banco-imagens', 'list', query),
    detail: (idImage) => resourceKey('admin-banco-imagens', 'banco-imagens', 'detail', idImage),
  },
  bancoImagens: {
    all: (scopeKey = 'client') => resourceKey(scopeKey, 'banco-imagens'),
    list: (query = {}, scopeKey = 'client') => resourceKey(scopeKey, 'banco-imagens', 'list', query),
    detail: (idImage, scopeKey = 'client') => resourceKey(scopeKey, 'banco-imagens', 'detail', idImage),
  },
  cliente: (scopeKey = 'client') => resourceKey(scopeKey, 'cliente'),
  terminais: {
    all: (scopeKey = 'client') => resourceKey(scopeKey, 'terminais'),
    list: (query = {}, scopeKey = 'client') => resourceKey(scopeKey, 'terminais', 'list', query),
    resumo: (query = {}, scopeKey = 'client') => resourceKey(scopeKey, 'terminais', 'resumo', query),
    detail: (idTerminal, scopeKey = 'client') => resourceKey(scopeKey, 'terminais', 'detail', idTerminal),
    diagnostico: (idTerminal, query = {}, scopeKey = 'client') =>
      resourceKey(scopeKey, 'terminais', 'diagnostico', idTerminal, query),
  },
  previsualizacao: (query = {}, scopeKey = 'client') => resourceKey(scopeKey, 'previsualizacao', query),
  midias: {
    all: (scopeKey = 'client') => resourceKey(scopeKey, 'midias'),
    list: (query = {}, scopeKey = 'client') => resourceKey(scopeKey, 'midias', 'list', query),
    detail: (idMedia, scopeKey = 'client') => resourceKey(scopeKey, 'midias', 'detail', idMedia),
    impacto: (idMedia, scopeKey = 'client') => resourceKey(scopeKey, 'midias', 'impacto', idMedia),
  },
  programacoes: {
    all: (scopeKey = 'client') => resourceKey(scopeKey, 'programacoes'),
    list: (query = {}, scopeKey = 'client') => resourceKey(scopeKey, 'programacoes', 'list', query),
    resumo: (query = {}, scopeKey = 'client') => resourceKey(scopeKey, 'programacoes', 'resumo', query),
    detail: (idSchedule, scopeKey = 'client') => resourceKey(scopeKey, 'programacoes', 'detail', idSchedule),
    playlist: (idSchedule, scopeKey = 'client') => resourceKey(scopeKey, 'programacoes', 'playlist', idSchedule),
    terminais: (idSchedule, scopeKey = 'client') => resourceKey(scopeKey, 'programacoes', 'terminais', idSchedule),
  },
  monitoramento: {
    list: (query = {}, scopeKey = 'client') => resourceKey(scopeKey, 'monitoramento', 'list', query),
    resumo: (query = {}, scopeKey = 'client') => resourceKey(scopeKey, 'monitoramento', 'resumo', query),
  },
  conteudos: {
    list: (query = {}, scopeKey = 'client') => resourceKey(scopeKey, 'conteudos', 'list', query),
    detail: (idContent, scopeKey = 'client') => resourceKey(scopeKey, 'conteudos', 'detail', idContent),
    mascaras: (scopeKey = 'client') => resourceKey(scopeKey, 'conteudos', 'mascaras'),
  },
  mural: {
    mensagens: (query = {}, scopeKey = 'client') => resourceKey(scopeKey, 'mural', 'mensagens', query),
    destinatarios: (scopeKey = 'client') => resourceKey(scopeKey, 'mural', 'destinatarios'),
  },
};

const withTenantLinkState = (query) => ({
  ...query,
  tenantNotLinked: isAnywhereTenantNotLinkedError(query.error),
  tenantNotLinkedMessage: isAnywhereTenantNotLinkedError(query.error) ? query.error.message : null,
});

/**
 * Shared query hook. `tenantNotLinked` is explicit so a page can show the
 * setup state (rather than a generic retry card) for the 409 contract case.
 */
export const useAnywhereQuery = (queryKey, queryFn, options = {}) => {
  // React Query's `keepPreviousData` is useful while paging a single client, but it would display
  // client X's result while an administrative URL for client Y is fetching.  The scope is part of
  // every key; disable that presentation shortcut whenever the key denotes an admin scope so a
  // rapid selector change has an explicit loading/empty state instead of a cross-client flash.
  const scopeKey = queryKey?.[1];
  const isAdministrativeScope = String(scopeKey || '').startsWith('/anywhere/admin/clientes/');
  const query = useQuery({
    queryKey,
    queryFn,
    ...(isAdministrativeScope ? { ...options, keepPreviousData: false } : options),
  });
  return withTenantLinkState(query);
};

/**
 * Shared mutation hook. Mutations invalidate the module root by default: data
 * such as summaries and impact panels may change outside the resource edited.
 * Pass `{ invalidate: false }` only when a caller updates all relevant cache.
 */
export const useAnywhereMutation = (mutationFn, options = {}, scopeKey = 'client') => {
  const queryClient = useQueryClient();
  const { invalidate = true, onSuccess, ...mutationOptions } = options;
  const mutation = useMutation({
    mutationFn,
    ...mutationOptions,
    onSuccess: async (...args) => {
      if (invalidate) await queryClient.invalidateQueries({ queryKey: anywhereKeys.root(scopeKey) });
      if (onSuccess) return onSuccess(...args);
      return undefined;
    },
  });

  return {
    ...mutation,
    tenantNotLinked: isAnywhereTenantNotLinkedError(mutation.error),
    tenantNotLinkedMessage: isAnywhereTenantNotLinkedError(mutation.error) ? mutation.error.message : null,
  };
};

// Fetch hooks
export const useCliente = (options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(scopedAnywhereKey(anywhereKeys.cliente(), scopeKey), service.getCliente, options);
};

/**
 * Clientes administrativos são uma leitura multi-cliente e não podem usar a chave de um cliente
 * selecionado. O prefixo próprio também permite invalidar a lista após criar/editar/excluir.
 */
export const useAdminClientes = (query, options) => {
  const { service } = useAnywhereScope();
  return useAnywhereQuery(anywhereKeys.adminClientes.list(query), () => service.listAdminClientes(query), options);
};

export const useAdminCliente = (idClient, options) => {
  const { service } = useAnywhereScope();
  return useAnywhereQuery(anywhereKeys.adminClientes.detail(idClient), () => service.getAdminCliente(idClient), {
    enabled: Boolean(idClient),
    ...options,
  });
};
/** Leitura de mídias de todos os clientes: chave própria, fora de qualquer cliente selecionado. */
export const useAdminMidias = (query, options) => {
  const { service } = useAnywhereScope();
  return useAnywhereQuery(anywhereKeys.adminMidias.list(query), () => service.listAdminMidias(query), options);
};

export const useAdminMidia = (idMedia, options) => {
  const { service } = useAnywhereScope();
  return useAnywhereQuery(anywhereKeys.adminMidias.detail(idMedia), () => service.getAdminMidia(idMedia), {
    enabled: Boolean(idMedia),
    ...options,
  });
};

export const useAdminMidiaClientes = (idMedia, options) => {
  const { service } = useAnywhereScope();
  return useAnywhereQuery(anywhereKeys.adminMidias.clientes(idMedia), () => service.getAdminMidiaClientes(idMedia), {
    enabled: Boolean(idMedia),
    ...options,
  });
};

export const useAdminBancoImagens = (query, options) => {
  const { service } = useAnywhereScope();
  return useAnywhereQuery(
    anywhereKeys.adminBancoImagens.list(query),
    () => service.listAdminBancoImagens(query),
    options
  );
};

export const useAdminImagemBanco = (idImage, options) => {
  const { service } = useAnywhereScope();
  return useAnywhereQuery(anywhereKeys.adminBancoImagens.detail(idImage), () => service.getAdminImagemBanco(idImage), {
    enabled: Boolean(idImage),
    ...options,
  });
};

export const useBancoImagens = (query, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.bancoImagens.list(query), scopeKey),
    () => service.listBancoImagens(query),
    options
  );
};

export const useImagemBanco = (idImage, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.bancoImagens.detail(idImage), scopeKey),
    () => service.getImagemBanco(idImage),
    { enabled: Boolean(idImage), ...options }
  );
};

export const useTerminais = (query, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.terminais.list(query), scopeKey),
    () => service.listTerminais(query),
    options
  );
};
export const useTerminaisResumo = (query, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.terminais.resumo(query), scopeKey),
    () => service.getTerminaisResumo(query),
    options
  );
};
export const useTerminal = (idTerminal, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.terminais.detail(idTerminal), scopeKey),
    () => service.getTerminal(idTerminal),
    {
      enabled: Boolean(idTerminal),
      ...options,
    }
  );
};
export const useDiagnosticoTerminal = (idTerminal, query, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.terminais.diagnostico(idTerminal, query), scopeKey),
    () => service.getDiagnosticoTerminal(idTerminal, query),
    { enabled: Boolean(idTerminal), ...options }
  );
};
export const usePrevisualizacao = (query, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.previsualizacao(query), scopeKey),
    () => service.getPrevisualizacao(query),
    {
      enabled: Boolean(query?.idTerminal),
      ...options,
    }
  );
};
export const useMidias = (query, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.midias.list(query), scopeKey),
    () => service.listMidias(query),
    options
  );
};
export const useMidia = (idMedia, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.midias.detail(idMedia), scopeKey),
    () => service.getMidia(idMedia),
    {
      enabled: Boolean(idMedia),
      ...options,
    }
  );
};
export const useImpactoMidia = (idMedia, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.midias.impacto(idMedia), scopeKey),
    () => service.getImpactoMidia(idMedia),
    {
      enabled: Boolean(idMedia),
      ...options,
    }
  );
};
export const useProgramacoes = (query, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.programacoes.list(query), scopeKey),
    () => service.listProgramacoes(query),
    options
  );
};
export const useProgramacoesResumo = (query, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.programacoes.resumo(query), scopeKey),
    () => service.getProgramacoesResumo(query),
    options
  );
};
export const useProgramacao = (idSchedule, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.programacoes.detail(idSchedule), scopeKey),
    () => service.getProgramacao(idSchedule),
    {
      enabled: Boolean(idSchedule),
      ...options,
    }
  );
};
export const usePlaylist = (idSchedule, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.programacoes.playlist(idSchedule), scopeKey),
    () => service.getPlaylist(idSchedule),
    {
      enabled: Boolean(idSchedule),
      ...options,
    }
  );
};
export const useProgramacaoTerminais = (idSchedule, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.programacoes.terminais(idSchedule), scopeKey),
    () => service.getProgramacaoTerminais(idSchedule),
    { enabled: Boolean(idSchedule), ...options }
  );
};
export const useMonitoramento = (query, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.monitoramento.list(query), scopeKey),
    () => service.listMonitoramento(query),
    options
  );
};
export const useMonitoramentoResumo = (query, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.monitoramento.resumo(query), scopeKey),
    () => service.getMonitoramentoResumo(query),
    options
  );
};
export const useConteudos = (query, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.conteudos.list(query), scopeKey),
    () => service.listConteudos(query),
    options
  );
};
export const useConteudo = (idContent, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.conteudos.detail(idContent), scopeKey),
    () => service.getConteudo(idContent),
    {
      enabled: Boolean(idContent),
      ...options,
    }
  );
};
export const useMascarasConteudo = (options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.conteudos.mascaras(), scopeKey),
    () => service.listMascarasConteudo(),
    options
  );
};
export const useMuralMensagens = (query, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.mural.mensagens(query), scopeKey),
    () => service.listMuralMensagens(query),
    options
  );
};
export const useMuralDestinatarios = (options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereQuery(
    scopedAnywhereKey(anywhereKeys.mural.destinatarios(), scopeKey),
    service.listMuralDestinatarios,
    options
  );
};

// Mutation hooks. Multi-part mutations accept the same names used by the DTOs.
const useScopedMutation = (factory, options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereMutation(factory(service), options, scopeKey);
};

const useAdminMutation = (factory, options) => {
  const { service } = useAnywhereScope();
  return useAnywhereMutation(factory(service), options, 'admin-clientes');
};

/**
 * Compartilhar e trocar máscara mudam dados de vários clientes de uma vez (vínculos, playlists,
 * conteúdos, contadores do cadastro). Por isso invalidam a raiz inteira do módulo, e não só a chave
 * administrativa: uma tela de cliente aberta não pode continuar mostrando o estado anterior.
 */
const useAdminMidiaMutation = (factory, options = {}) => {
  const { service } = useAnywhereScope();
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options;
  return useAnywhereMutation(
    factory(service),
    {
      ...mutationOptions,
      invalidate: false,
      onSuccess: async (...args) => {
        await queryClient.invalidateQueries({ queryKey: ROOT_KEY });
        if (onSuccess) return onSuccess(...args);
        return undefined;
      },
    },
    'admin-midias'
  );
};

export const useShareAdminMidia = (options) =>
  useAdminMidiaMutation(
    (service) =>
      ({ idMedia, idClient }) =>
        service.shareAdminMidia(idMedia, { idClient }),
    options
  );
export const useRevokeAdminMidia = (options) =>
  useAdminMidiaMutation(
    (service) =>
      ({ idMedia, idClient }) =>
        service.revokeAdminMidia(idMedia, { idClient }),
    options
  );
export const useTrocarMascaraAdminMidia = (options) =>
  useAdminMidiaMutation(
    (service) =>
      ({ idMedia, replacementIdMedia }) =>
        service.trocarMascaraAdminMidia(idMedia, { replacementIdMedia }),
    options
  );

/**
 * Banco de imagens: uma imagem global aparece para todos os clientes e um conteúdo aponta para o
 * arquivo, então qualquer mutação invalida a raiz inteira — uma tela de cliente aberta não pode
 * continuar mostrando o estado anterior.
 */
const useBancoImagensMutation = (factory, options = {}) => {
  const { service, scopeKey } = useAnywhereScope();
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options;
  return useAnywhereMutation(
    factory(service),
    {
      ...mutationOptions,
      invalidate: false,
      onSuccess: async (...args) => {
        await queryClient.invalidateQueries({ queryKey: ROOT_KEY });
        if (onSuccess) return onSuccess(...args);
        return undefined;
      },
    },
    scopeKey
  );
};

export const useUploadImagemBanco = (options) =>
  useBancoImagensMutation((service) => service.uploadImagemBanco, options);
export const useUpdateImagemBanco = (options) =>
  useBancoImagensMutation(
    (service) =>
      ({ idImage, body }) =>
        service.updateImagemBanco(idImage, body),
    options
  );
export const useToggleImagemBancoStatus = (options) =>
  useBancoImagensMutation(
    (service) =>
      ({ idImage, body }) =>
        service.toggleImagemBancoStatus(idImage, body),
    options
  );
export const useDeleteImagemBanco = (options) =>
  useBancoImagensMutation((service) => service.deleteImagemBanco, options);
export const useEditarImagemBanco = (options) =>
  useBancoImagensMutation(
    (service) =>
      ({ idImage, body }) =>
        service.editarImagemBanco(idImage, body),
    options
  );
export const useEditarImagemMidia = (options) =>
  useBancoImagensMutation(
    (service) =>
      ({ idMedia, body }) =>
        service.editarImagemMidia(idMedia, body),
    options
  );
export const useUploadAdminImagemBanco = (options) =>
  useBancoImagensMutation((service) => service.uploadAdminImagemBanco, options);
export const useUpdateAdminImagemBanco = (options) =>
  useBancoImagensMutation(
    (service) =>
      ({ idImage, body }) =>
        service.updateAdminImagemBanco(idImage, body),
    options
  );
export const useToggleAdminImagemBancoStatus = (options) =>
  useBancoImagensMutation(
    (service) =>
      ({ idImage, body }) =>
        service.toggleAdminImagemBancoStatus(idImage, body),
    options
  );
export const useDeleteAdminImagemBanco = (options) =>
  useBancoImagensMutation((service) => service.deleteAdminImagemBanco, options);
export const useEditarAdminImagemBanco = (options) =>
  useBancoImagensMutation(
    (service) =>
      ({ idImage, body }) =>
        service.editarAdminImagemBanco(idImage, body),
    options
  );

export const useCreateAdminCliente = (options) => useAdminMutation((service) => service.createAdminCliente, options);
export const useUpdateAdminCliente = (options) =>
  useAdminMutation(
    (service) =>
      ({ idClient, body }) =>
        service.updateAdminCliente(idClient, body),
    options
  );
export const useDeleteAdminCliente = (options) => useAdminMutation((service) => service.deleteAdminCliente, options);

export const useCreateTerminal = (options) => useScopedMutation((service) => service.createTerminal, options);
export const useUpdateTerminal = (options) =>
  useScopedMutation(
    (service) =>
      ({ idTerminal, body }) =>
        service.updateTerminal(idTerminal, body),
    options
  );
export const useToggleTerminalStatus = (options) =>
  useScopedMutation(
    (service) =>
      ({ idTerminal, body }) =>
        service.toggleTerminalStatus(idTerminal, body),
    options
  );
export const useDeleteTerminal = (options) => useScopedMutation((service) => service.deleteTerminal, options);
export const useForcarAtualizacaoTerminal = (options) =>
  useScopedMutation(
    (service) =>
      ({ idTerminal, body }) =>
        service.forcarAtualizacaoTerminal(idTerminal, body),
    options
  );
export const useSolicitarCapturaTerminal = (options) =>
  useScopedMutation((service) => service.solicitarCapturaTerminal, options);
export const useUpdateMidia = (options) =>
  useScopedMutation(
    (service) =>
      ({ idMedia, body }) =>
        service.updateMidia(idMedia, body),
    options
  );
export const useToggleMidiaStatus = (options) =>
  useScopedMutation(
    (service) =>
      ({ idMedia, body }) =>
        service.toggleMidiaStatus(idMedia, body),
    options
  );
export const useDeleteMidia = (options) => useScopedMutation((service) => service.deleteMidia, options);
export const useUploadMidia = (options) => useScopedMutation((service) => service.uploadMidia, options);
export const useCreateProgramacao = (options) => useScopedMutation((service) => service.createProgramacao, options);
export const useUpdateProgramacao = (options) =>
  useScopedMutation(
    (service) =>
      ({ idSchedule, body }) =>
        service.updateProgramacao(idSchedule, body),
    options
  );
export const useToggleProgramacaoStatus = (options) =>
  useScopedMutation(
    (service) =>
      ({ idSchedule, body }) =>
        service.toggleProgramacaoStatus(idSchedule, body),
    options
  );
export const useDuplicarProgramacao = (options) =>
  useScopedMutation(
    (service) =>
      ({ idSchedule, body }) =>
        service.duplicarProgramacao(idSchedule, body),
    options
  );
export const useDeleteProgramacao = (options) => useScopedMutation((service) => service.deleteProgramacao, options);
export const useAddPlaylistItem = (options) =>
  useScopedMutation(
    (service) =>
      ({ idSchedule, body }) =>
        service.addPlaylistItem(idSchedule, body),
    options
  );

/**
 * F7 — incluir N mídias numa posição, em N chamadas de `POST .../playlist`.
 *
 * O contrato só tem `AddPlaylistItemBody` com UMA `idMedia`. Incluir três mídias na posição da
 * `mediaOrdem` 12 é POST em 12, 13 e 14, nessa sequência: cada inclusão empurra `mediaOrdem >= alvo`
 * em 1, então o alvo seguinte é o anterior + 1. `order` OMITIDO significa "no fim", e é omitido de
 * propósito — assim o back recalcula `max + 1` dentro da própria transação, em vez de o cliente
 * mandar um valor que um retrato defasado poderia colocar além do máximo, abrindo buraco de ordem.
 *
 * ⚠️ `PUT .../playlist` NÃO serve para incluir: ele regrava a playlist inteira como 1..n e normaliza
 * a ordem (Q5). É a operação de "trocar todas".
 *
 * ⚠️ **Falha no meio do lote não é atômica** — não há transação entre POSTs. Se a primeira falhar,
 * o erro sobe (a tela mostra o 401/409 como em qualquer mutação). Se falhar depois de algumas terem
 * entrado, a mutação RESOLVE com `{ playlist, added, requested, error }`: a playlist autoritativa
 * já aplicada precisa chegar ao cache, e a tela precisa dizer "2 de 3". Quem consome tem que ler
 * `result.error` — `mutation.error` fica nulo nesse caso.
 */
export const useAddPlaylistItems = (options) => {
  const { service, scopeKey } = useAnywhereScope();
  return useAnywhereMutation(
    async ({ idSchedule, idMedias, order }) => {
      let playlist = null;
      let added = 0;
      for (let index = 0; index < idMedias.length; index += 1) {
        const body =
          order === undefined ? { idMedia: idMedias[index] } : { idMedia: idMedias[index], order: order + index };
        try {
          // eslint-disable-next-line no-await-in-loop
          playlist = await service.addPlaylistItem(idSchedule, body);
          added += 1;
        } catch (error) {
          if (added === 0) throw error;
          return { playlist, added, requested: idMedias.length, error };
        }
      }
      return { playlist, added, requested: idMedias.length, error: null };
    },
    options,
    scopeKey
  );
};
export const useReplacePlaylist = (options) =>
  useScopedMutation(
    (service) =>
      ({ idSchedule, body }) =>
        service.replacePlaylist(idSchedule, body),
    options
  );
export const useReplacePlaylistItem = (options) =>
  useScopedMutation(
    (service) =>
      ({ idSchedule, idScheduleMedia, body }) =>
        service.replacePlaylistItem(idSchedule, idScheduleMedia, body),
    options
  );
export const useDeletePlaylistItem = (options) =>
  useScopedMutation(
    (service) =>
      ({ idSchedule, idScheduleMedia }) =>
        service.deletePlaylistItem(idSchedule, idScheduleMedia),
    options
  );
export const useMovePlaylistItemUp = (options) =>
  useScopedMutation(
    (service) =>
      ({ idSchedule, idScheduleMedia }) =>
        service.movePlaylistItemUp(idSchedule, idScheduleMedia),
    options
  );
export const useMovePlaylistItemDown = (options) =>
  useScopedMutation(
    (service) =>
      ({ idSchedule, idScheduleMedia }) =>
        service.movePlaylistItemDown(idSchedule, idScheduleMedia),
    options
  );
export const useReorderPlaylistItem = (options) =>
  useScopedMutation(
    (service) =>
      ({ idSchedule, idScheduleMedia, body }) =>
        service.reorderPlaylistItem(idSchedule, idScheduleMedia, body),
    options
  );
export const useAddProgramacaoTerminais = (options) =>
  useScopedMutation(
    (service) =>
      ({ idSchedule, body }) =>
        service.addProgramacaoTerminais(idSchedule, body),
    options
  );
export const useAddProgramacaoTerminaisPorResolucao = (options) =>
  useScopedMutation(
    (service) =>
      ({ idSchedule, body }) =>
        service.addProgramacaoTerminaisPorResolucao(idSchedule, body),
    options
  );
export const useRemoveProgramacaoTerminal = (options) =>
  useScopedMutation(
    (service) =>
      ({ idSchedule, idTerminal }) =>
        service.removeProgramacaoTerminal(idSchedule, idTerminal),
    options
  );
export const useUpdateConteudo = (options) =>
  useScopedMutation(
    (service) =>
      ({ idContent, body }) =>
        service.updateConteudo(idContent, body),
    options
  );
export const useToggleConteudoStatus = (options) =>
  useScopedMutation(
    (service) =>
      ({ idContent, body }) =>
        service.toggleConteudoStatus(idContent, body),
    options
  );
// A imagem nova do conteúdo entra no banco de imagens: invalida a raiz, como as mutações do banco.
export const useCreateConteudo = (options) => useBancoImagensMutation((service) => service.createConteudo, options);
export const useDeleteConteudo = (options) => useScopedMutation((service) => service.deleteConteudo, options);
export const useCreateMuralMensagem = (options) => useScopedMutation((service) => service.createMuralMensagem, options);
